import { connect } from 'react-redux'
import { View, type ViewStyle } from 'react-native'
import IconCard from 'APP/Converse/IconCard'
import Typography from 'APP/Converse/Typography'
import styled from 'styled-components/native'

import I18n, { isTranslationKey } from 'APP/I18n'
import {
  getDisplayableBrands,
  getExternalBrandConfig,
  openExternalBrandApp,
} from 'APP/Lib/BrandHelpers'
import type { RootState } from 'APP/Store/CreateStore'

interface AppLaunchpadProps {
  eligibleBrands: string[]
  crossBrandBrands?: string[]
  style?: ViewStyle
  isEligible: boolean
  hasEligibilityIntervals: boolean
}

const LaunchpadHeader = styled(Typography)`
  color: ${({ theme }) => theme.colors.text};
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
`

const LaunchpadCardContainer = styled(View)`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin * 0.5}px;
`

function AppLaunchpad({
  eligibleBrands,
  crossBrandBrands,
  style,
  isEligible,
  hasEligibilityIntervals,
}: AppLaunchpadProps) {
  const renderLaunchpadCard = (brandId: string) => {
    const bodyKey = `AppLaunchpad.${brandId}.body`
    const { productName: appName } = getExternalBrandConfig(brandId, I18n.baseLocale)

    return (
      <LaunchpadCardContainer key={brandId}>
        <IconCard
          testID={`APP_LAUNCHPAD_${brandId}`}
          title={appName}
          description={isTranslationKey(bodyKey) ? I18n.t(bodyKey) : undefined}
          rightIcon={{ src: 'exit-to-app', variant: 'materialIcons' }}
          onPress={() => openExternalBrandApp(appName, brandId)}
          accessibilityRole="button"
        />
      </LaunchpadCardContainer>
    )
  }

  const filteredBrands = getDisplayableBrands(eligibleBrands, crossBrandBrands)
  // InactiveInfoCard (rendered by InactiveChatInfoCard) already lists the other brands
  // for users whose coverage lapsed (users will see "Your coverage has been migrated to: X");
  // users with no coverage history still need the launchpad
  if (!isEligible && hasEligibilityIntervals) return null

  return (
    (filteredBrands && filteredBrands.length > 0 && (
      <View style={style}>
        <LaunchpadHeader variant="h3">{I18n.t('AppLaunchpad.header')}</LaunchpadHeader>
        {filteredBrands.map((brand) => renderLaunchpadCard(brand))}
      </View>
    )) ||
    null
  )
}

const mapStateToProps = ({ patient, features }: RootState) => {
  const eligibleServices = patient?.profile?.eligibleServices
  const eligibilityIntervals = patient?.profile?.eligibilityIntervals

  return {
    eligibleBrands: patient?.profile?.eligibleBrands ?? [],
    crossBrandBrands: features?.crossBrandEligibilityBrands,
    isEligible: !!(eligibleServices && Object.keys(eligibleServices).length > 0),
    hasEligibilityIntervals: eligibilityIntervals?.length > 0,
  }
}

export default connect(mapStateToProps)(AppLaunchpad)
